import type { FunctionComponent, ReactNode } from 'react';
import type React from 'react';
import type { ItemSelectIF } from '@/formbuilder/ItemSelectFormComponent.atom';
import { ItemSelectFormComponent } from '@/formbuilder/ItemSelectFormComponent.atom';
import type { ItemSelectAndSearchIF } from '@/formbuilder/ItemSelectAndSearchFormComponent.atom';
import { ItemSelectAndSearchFormComponent } from '@/formbuilder/ItemSelectAndSearchFormComponent.atom';
import type { ValueWithUnitsProps } from '@/formbuilder/ValueWithUnitsFormComponent';
import { ValueWithUnitsFormComponent } from '@/formbuilder/ValueWithUnitsFormComponent';
import type { ValueWithSymbolIF } from '@/formbuilder/ValueWithSymbolFormComponent';
import { ValueWithSymbolFormComponent } from '@/formbuilder/ValueWithSymbolFormComponent';
import type { SearchTitleIF } from '@/formbuilder/SearchTitleFormComponent';
import { SearchTitleFormComponent } from '@/formbuilder/SearchTitleFormComponent';
import type { LabelIF } from '@/formbuilder/LabelFormComponent.atom';
import { LabelFormComponent } from '@/formbuilder/LabelFormComponent.atom';
import type { PatternIF } from '@/formbuilder/PatternFormComponent';
import { PatternFormComponent } from '@/formbuilder/PatternFormComponent';
import type { SelectIF } from '@/formbuilder/SelectFormComponent';
import { SelectFormComponent } from '@/formbuilder/SelectFormComponent';
import type { RadioButtonsIF } from '@/formbuilder/RadioButtonGroupFormComponent.atom';
import { RadioButtonGroupFormComponent } from '@/formbuilder/RadioButtonGroupFormComponent.atom';
import type { CheckboxIF } from '@/formbuilder/CheckboxFormComponent.atom';
import { CheckboxFormComponent } from '@/formbuilder/CheckboxFormComponent.atom';
import type { IconSelectProps } from '@/formbuilder/IconSelectFormComponent.atom';
import { IconSelectFormComponent } from '@/formbuilder/IconSelectFormComponent.atom';
import type { OperatorSelectIF } from '@/formbuilder/OperatorSelectFormComponent.atom';
import { OperatorSelectFormComponent } from '@/formbuilder/OperatorSelectFormComponent.atom';
import type { TextFieldIF } from '@/formbuilder/TextFieldFormComponent.atom';
import { TextFieldFormComponent } from '@/formbuilder/TextFieldFormComponent.atom';
import type { FormControlJsonIF } from '@/formbuilder/JsonTextAreaFormComponent.atom';
import { JsonTextAreaFormComponent } from '@/formbuilder/JsonTextAreaFormComponent.atom';
import type { ErrorMessageIF } from '@/formbuilder/ErrorMessageFormComponent.atom';
import { ErrorMessageFormComponent } from '@/formbuilder/ErrorMessageFormComponent.atom';
import type { PasswordCheckIF } from '@/formbuilder/PasswordCheckFormComponent.atom';
import { PasswordCheckFormComponent } from '@/formbuilder/PasswordCheckFormComponent.atom';
import type { PasswordGroupIF } from '@/formbuilder/PasswordGroupFormComponent';
import { PasswordGroupFormComponent } from '@/formbuilder/PasswordGroupFormComponent';
import type { SelectIdentityProps } from '@/formbuilder/SelectIdentityFormComponent.atom';
import { SelectIdentityFormComponent } from '@/formbuilder/SelectIdentityFormComponent.atom';
import type { TimeZoneSelectorIF } from '@/formbuilder/TimeZoneSelectorFormComponent.atom';
import { TimeZoneSelectorFormComponent } from '@/formbuilder/TimeZoneSelectorFormComponent.atom';
import type { ClickableLinkIF } from '@/formbuilder/ClickableLinkFormComponent.atom';
import { ClickableLinkFormComponent } from '@/formbuilder/ClickableLinkFormComponent.atom';
import type { ToolStore } from '@/core/AdvancedParameters.atom';
import type { CheckboxTableIF } from '@/formbuilder/CheckboxTableFormComponent.atom';
import { CheckboxTableFormComponent } from '@/formbuilder/CheckboxTableFormComponent.atom';
import type { ButtonGroupIF } from '@/formbuilder/ButtonGroupFormComponent';
import { ButtonGroupFormComponent } from '@/formbuilder/ButtonGroupFormComponent';
import type { SelectUnitIF } from '@/formbuilder/SelectUnitFormComponent.atom';
import { SelectUnitFormComponent } from '@/formbuilder/SelectUnitFormComponent.atom';
import type { FileDropperIF } from '@/formbuilder/FileDropperFormComponent.atom';
import { FileDropperFormComponent } from '@/formbuilder/FileDropperFormComponent.atom';
import type { CompositesSelectorIF } from '@/formbuilder/CompositeLogicSelectorFormComponent.molecule';
import { CompositesSelectorFormComponent } from '@/formbuilder/CompositeLogicSelectorFormComponent.molecule';
import type { MaxCapsuleDurationIF } from '@/formbuilder/MaxCapsuleDurationFormComponent';
import { MaxCapsuleDurationFormComponent } from '@/formbuilder/MaxCapsuleDurationFormComponent';
import type { IconIF } from '@/formbuilder/IconFormComponent.atom';
import { IconFormComponent } from '@/formbuilder/IconFormComponent.atom';
import type { CapsuleInputIF } from '@/formbuilder/CapsuleInputFormComponent.molecule';
import { CapsuleInputFormComponent } from '@/formbuilder/CapsuleInputFormComponent.molecule';
import type { InputTableIF } from '@/formbuilder/InputTableFormComponent.molecule';
import { InputTableFormComponent } from '@/formbuilder/InputTableFormComponent.molecule';
import type { StatisticSelectorIF } from '@/formbuilder/StatisticSelectorFormComponent.atom';
import { StatisticSelectorFormComponent } from '@/formbuilder/StatisticSelectorFormComponent.atom';
import type { ColorPickerIF } from '@/formbuilder/ColorPickerFormComponent.molecule';
import { ColorPickerFormComponent } from '@/formbuilder/ColorPickerFormComponent.molecule';
import type { PriorityPickerIF } from '@/formbuilder/PriorityPickerFormComponent.molecule';
import { PriorityPickerFormComponent } from '@/formbuilder/PriorityPickerFormComponent.molecule';
import type { FormulaVariablesIF } from '@/formbuilder/FormulaVariablesFormComponent';
import { FormulaVariablesFormComponent } from '@/formbuilder/FormulaVariablesFormComponent';
import type { FormulaEditorIF } from '@/formbuilder/FormulaEditorFormComponent';
import { FormulaEditorFormComponent } from '@/formbuilder/FormulaEditorFormComponent';
import { DisplayOnlyFormElementWrapper } from '@/formbuilder/formBuildingBlocks';
import type { ImageSelectIF } from '@/formbuilder/ImageSelectFormComponent';
import { ImageSelectFormComponent } from '@/formbuilder/ImageSelectFormComponent';
import type { CapsuleGroupIF } from '@/formbuilder/CapsuleGroupFormComponent';
import { CapsuleGroupFormComponent } from '@/formbuilder/CapsuleGroupFormComponent';
import type { SearchWidgetIF } from '@/formbuilder/SearchWidgetFormComponent';
import { SearchWidgetFormComponent } from '@/formbuilder/SearchWidgetFormComponent';
import type { ScatterPlotRegionIF } from '@/formbuilder/ScatterPlotRegionFormComponent';
import { ScatterPlotRegionFormComponent } from '@/formbuilder/ScatterPlotRegionFormComponent';
import type { DateTimeRangeIF } from '@/formbuilder/DateTimeRangeFormComponent';
import { DateTimeRangeFormComponent } from '@/formbuilder/DateTimeRangeFormComponent';
import type { DateTimeEntryIF } from '@/formbuilder/DateTimeEntryFormComponent';
import { DateTimeEntryFormComponent } from '@/formbuilder/DateTimeEntryFormComponent';

import type { ValueBinsIF } from '@/formbuilder/ValueBinsFormComponent';
import { ValueBinsFormComponent } from '@/formbuilder/ValueBinsFormComponent';
import type { ButtonWithPopoverIF } from '@/formbuilder/ButtonWithPopoverFormComponent';
import { ButtonWithPopoverFormComponent } from '@/formbuilder/ButtonWithPopoverFormComponent';
import type { SelectItemSearchFormComponentProps } from '@/formbuilder/SelectItemSearchFormComponent';
import { SelectItemSearchFormComponent } from '@/formbuilder/SelectItemSearchFormComponent';
import type { LowerUpperLimitsIF } from '@/formbuilder/LowerUpperLimitsFormComponent';
import { LowerUpperLimitsFormComponent } from '@/formbuilder/LowerUpperLimitsFormComponent';
import type { SelectFolderProps } from '@/formbuilder/SelectFolderFormComponent';
import { SelectFolderFormComponent } from '@/formbuilder/SelectFolderFormComponent';
import type { SelectWorkbenchProps } from '@/formbuilder/SelectWorkbookFormComponent';
import { SelectWorkbookFormComponent } from '@/formbuilder/SelectWorkbookFormComponent';
import type { CopyableInputProps } from '@/formbuilder/CopyableInputFormComponent.molecule';
import { CopyableInputFormComponent } from '@/formbuilder/CopyableInputFormComponent.molecule';
import type { ScheduleFormComponentProps } from '@/formbuilder/ScheduleFormComponent';
import { ScheduleFormComponent } from '@/formbuilder/ScheduleFormComponent';
import type { DurationFormComponentProps } from '@/formbuilder/DurationFormComponent';
import { DurationFormComponent } from '@/formbuilder/DurationFormComponent';
import type { InputWithLinkedButtonsProps } from '@/formbuilder/InputWithLinkedButtonsFormComponent';
import { InputWithLinkedButtonsFormComponent } from '@/formbuilder/InputWithLinkedButtonsFormComponent';
import type { DatePickerFormComponentProps } from '@/formbuilder/DatePickerFormComponent.atom';
import { DatePickerFormComponent } from '@/formbuilder/DatePickerFormComponent.atom';
import type { CKEditorFormComponentProps } from '@/formbuilder/CKEditorFormComponent';
import { CKEditorFormComponent } from '@/formbuilder/CKEditorFormComponent';
import type { DatasourceSelectIF } from '@/formbuilder/DatasourceSelectFormComponent.atom';
import { DatasourceSelectFormComponent } from '@/formbuilder/DatasourceSelectFormComponent.atom';
import type { ToggleIF } from '@/formbuilder/ToggleFormComponent.molecule';
import { ToggleFormComponent } from '@/formbuilder/ToggleFormComponent.molecule';
import type { ButtonVariant } from '@seeqdev/qomponents';
import type { LocationLookupFormComponentProps } from '@/formbuilder/LocationLookupFormComponent.atom';
import { LocationLookupFormComponent } from '@/formbuilder/LocationLookupFormComponent.atom';
import type { ToggleInputSelectItemSearchFormComponentProps } from '@/formbuilder/ToggleInputSelectItemSearchFormComponent';
import { ToggleInputSelectItemSearchFormComponent } from '@/formbuilder/ToggleInputSelectItemSearchFormComponent';
import type { DescriptionFormComponentProps } from '@/formbuilder/DescriptionFormComponent.atom';
import { DescriptionFormComponent } from '@/formbuilder/DescriptionFormComponent.atom';
import type { FormulaItemTableIF } from '@/formbuilder/FormulaItemTableFormComponent.atom';
import { FormulaItemTableFormComponent } from '@/formbuilder/FormulaItemTableFormComponent.atom';
import type { FlexLayoutSplitterProps } from '@/formbuilder/formElements/FlexLayoutSplitter';
import { FlexLayoutSplitter } from '@/formbuilder/formElements/FlexLayoutSplitter';
import type { BaseFormElement } from '@/formbuilder/formBuilder.types';

export const FORM_ERROR = 'Error';
export const FORM_WARNING = 'Warning';

export type { BaseFormElement, ValidatingFormComponent } from '@/formbuilder/formBuilder.types';

export const SUPPORTED_COMPONENTS = {
  CheckboxFormComponent,
  ErrorMessageFormComponent,
  TextFieldFormComponent,
  JsonTextAreaFormComponent,
  IconSelectFormComponent,
  ItemSelectFormComponent,
  LabelFormComponent,
  OperatorSelectFormComponent,
  RadioButtonGroupFormComponent,
  SearchTitleFormComponent,
  ValueWithUnitsFormComponent,
  TimeZoneSelectorFormComponent,
  ClickableLinkFormComponent,
  CheckboxTableFormComponent,
  SelectUnitFormComponent,
  FileDropperFormComponent,
  CompositesSelectorFormComponent,
  MaxCapsuleDurationFormComponent,
  IconFormComponent,
  CapsuleInputFormComponent,
  InputTableFormComponent,
  StatisticSelectorFormComponent,
  ColorPickerFormComponent,
  PriorityPickerFormComponent,
  ValueWithSymbolFormComponent,
  PatternFormComponent,
  SelectFormComponent,
  ButtonGroupFormComponent,
  DisplayOnlyFormElementWrapper,
  ImageSelectFormComponent,
  PasswordCheckFormComponent,
  PasswordGroupFormComponent,
  FormulaVariablesFormComponent,
  FormulaEditorFormComponent,
  CapsuleGroupFormComponent,
  SearchWidgetFormComponent,
  SelectIdentityFormComponent,
  ScatterPlotRegionFormComponent,
  DateTimeRangeFormComponent,
  DateTimeEntryFormComponent,
  ValueBinsFormComponent,
  ButtonWithPopoverFormComponent,
  SelectItemSearchFormComponent,
  SelectFolderFormComponent,
  SelectWorkbookFormComponent,
  CopyableInputFormComponent,
  ScheduleFormComponent,
  DurationFormComponent,
  InputWithLinkedButtonsFormComponent,
  DatePickerFormComponent,
  CKEditorFormComponent,
  DatasourceSelectFormComponent,
  ToggleFormComponent,
  ToggleInputSelectItemSearchFormComponent,
  LocationLookupFormComponent,
  DescriptionFormComponent,
  FormulaItemTableFormComponent,
  LowerUpperLimitsFormComponent,
  ItemSelectAndSearchFormComponent,
  FlexLayoutSplitter,
};

export interface ContinueButtonProps {
  /** This only applies to the "Save and Continue" Form: the label for the "submit and continue" button **/
  continueButtonLabel?: string;
  /** This only applies to the "Save and Continue" Form: if the "extra" submit button is enabled or not **/
  continueButtonEnabled?: boolean;
  /** This only applies to the "Save and Continue" Form: useful to control the display of the "extra" submit button **/
  continueButtonVariant?: ButtonVariant;
  /** This only applies to the "Save and Continue" Form: callback function for the "extra" submit button **/
  continueButtonOnSubmit?: (values: unknown) => void;
  /** This only applies to the "Save and Continue" Form: indicates where the "extra" submit button should be placed
   *  (relative to the "original" submit button **/
  continueButtonPosition?: 'left' | 'center' | 'right';
  /** This only applies to the "Save and Continue" Form: optional extraClassNames for the "extra" submit button**/
  continueButtonExtraClassNames?: string;
  /** This only applies to the "Save and Continue" Form: indicates if the "extra" submit button should be disabled **/
  continueButtonDisabled?: boolean;
  /** Specifies if the continue button should show a progress indicator after being clicked */
  continueProcessing?: boolean;
  /** icon classnames to apply to the continue button */
  continueButtonIcon?: string;
  /** tooltip shown when hovering over the continue button */
  continueButtonTooltip?: string;
}

export interface FormRow extends BaseFormElement {
  component: 'FormRow';
  components: FormElement[];
}

export interface RemovableFormRow extends BaseFormElement {
  component: 'RemovableFormRow';
  components: FormElement[];
  hideRemoveIcon?: boolean;
  removeAction: () => void;
  wrapperClassNames?: string;
}

export interface FormGroup extends BaseFormElement {
  component: 'FormGroup';
  formGroupExtraClassNames?: string;
  showGroupStyles?: boolean;
  components: FormElement[];
  showBracket?: boolean;
  showScroll?: boolean;
}

export interface FormGroupWithTitle extends BaseFormElement {
  component: 'FormGroupWithTitle';
  components: FormElement[];
  showBracket?: boolean;
  showScroll?: boolean;
  showTitle?: boolean;
  title?: string;
  titleLink?: string;
  titleLinkAction?: () => void;
  additionalTitleLink?: string;
  additionalTitleLinkAction?: () => void;
}

export interface AdvancedFormGroup extends BaseFormElement {
  component: 'AdvancedFormGroup';
  components: FormElement[];
  toolStore: ToolStore;
  toolName: string;
  toolId: string;
  alternateName?: string;
  iconStyle?: string;
}

export interface RemovableFormGroup extends BaseFormElement {
  component: 'RemovableFormGroup';
  components: FormElement[];
  hideIcon?: boolean;
  iconAction: () => void;
  iconHasMargin?: boolean;
  iconTooltip?: string;
  showLabelAfterRows?: boolean;
  labelAfterRows?: string;
  addBlockButtonAction?: () => void;
  addBlockButtonTooltip?: string;
  showAddBlockButton?: boolean;
}

export interface CollapsibleFormGroup extends BaseFormElement {
  component: 'CollapsibleFormGroup';
  components: FormElement[];
  deleteIconAction: () => void;
  hideDeleteIcon?: boolean;
  extraClassNames?: string;
  labelExtraClassNames?: string;
  isCollapsed: boolean;
  toggleCollapse: () => void;
  label: string;
  isLast?: boolean;
  isNewlyAdded?: boolean;
  newLabel?: string;
}

/**
 * this wrapper allows adding any child to a form and render it within form groups, rows, or blocks
 * it can only support "display only" children; no validation will be applied.
 * a typical use case for this are tables that only display information, or buttons that require no validation
 */
export interface DisplayOnlyFormElementWrapperIF extends BaseFormElement {
  component: 'DisplayOnlyFormElementWrapper';
  children: ReactNode;
}

export type FormElement =
  | FormGroup
  | FormGroupWithTitle
  | FormRow
  | AdvancedFormGroup
  | ItemSelectIF
  | ValueWithUnitsProps
  | SearchTitleIF
  | LabelIF
  | IconIF
  | RadioButtonsIF
  | CheckboxIF
  | IconSelectProps
  | CompositesSelectorIF
  | OperatorSelectIF
  | TextFieldIF
  | FormControlJsonIF
  | ErrorMessageIF
  | TimeZoneSelectorIF
  | CheckboxTableIF
  | ClickableLinkIF
  | SelectUnitIF
  | FileDropperIF
  | MaxCapsuleDurationIF
  | CapsuleInputIF
  | InputTableIF
  | RemovableFormGroup
  | RemovableFormRow
  | StatisticSelectorIF
  | ColorPickerIF
  | PriorityPickerIF
  | ValueWithSymbolIF
  | PatternIF
  | SelectIF
  | ButtonGroupIF
  | ImageSelectIF
  | PasswordCheckIF
  | FormulaVariablesIF
  | FormulaEditorIF
  | PasswordGroupIF
  | DisplayOnlyFormElementWrapperIF
  | CapsuleGroupIF
  | SearchWidgetIF
  | SelectIdentityProps
  | ScatterPlotRegionIF
  | DateTimeRangeIF
  | DateTimeEntryIF
  | ValueBinsIF
  | ButtonWithPopoverIF
  | SelectItemSearchFormComponentProps
  | SelectFolderProps
  | SelectWorkbenchProps
  | CopyableInputProps
  | ScheduleFormComponentProps
  | DurationFormComponentProps
  | InputWithLinkedButtonsProps
  | CollapsibleFormGroup
  | DatePickerFormComponentProps
  | CKEditorFormComponentProps
  | DatasourceSelectIF
  | LocationLookupFormComponentProps
  | ToggleIF
  | ToggleInputSelectItemSearchFormComponentProps
  | DescriptionFormComponentProps
  | FormulaItemTableIF
  | LowerUpperLimitsIF
  | ItemSelectAndSearchIF
  | FlexLayoutSplitterProps;
